import { keepPreviousData, queryOptions } from "@tanstack/react-query";

export interface ApiKeyPermission {
  id: string;
  description: string;
  category: string;
  createdAt?: string;
}

export interface ApiKeyV2 {
  id: string;
  name: string;
  description: string | null;
  prefix: string;
  status: "active" | "revoked";
  expiresAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  applicationId: string | null;
  applicationName: string | null;
  createdBy: string | null;
  creatorName: string | null;
  permissionCount: number;
}

export interface ApiKeysStats {
  total: number;
  active: number;
  revoked: number;
  expiringSoon: number;
}

/** GET /api/api-keys response envelope (list + aggregate stats). */
export interface ApiKeysResponse {
  keys: ApiKeyV2[];
  stats: ApiKeysStats;
}

/**
 * The API keys list for a status and search. The page keeps the previous rows on
 * screen while a filter change refetches (`keepPreviousData`).
 */
export function apiKeysQueryOptions(params: { status: string | undefined; search: string }) {
  return queryOptions({
    queryKey: ["admin", "api-keys", { status: params.status, search: params.search }],
    queryFn: async (): Promise<ApiKeysResponse> => {
      const search = new URLSearchParams();

      if (params.status) search.set("status", params.status);

      if (params.search) search.set("search", params.search);
      const res = await fetch(`/api/api-keys?${search.toString()}`, { cache: "no-store" });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // SAFETY: GET /api/api-keys returns the { keys, stats } envelope built
      // server-side per the route's documented response contract.
      return (await res.json()) as ApiKeysResponse;
    },
    placeholderData: keepPreviousData,
  });
}

/** Permission catalog, shared by the API keys picker and the Permissions API page. */
export const apiPermissionsQueryOptions = queryOptions({
  queryKey: ["admin", "api-permissions"],
  queryFn: async (): Promise<ApiKeyPermission[]> => {
    const res = await fetch("/api/permissions", { cache: "no-store" });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // SAFETY: GET /api/permissions returns a bare JSON array of permission
    // records; the guard keeps the legacy empty-list fallback.
    const data = (await res.json()) as ApiKeyPermission[];

    return Array.isArray(data) ? data : [];
  },
});

/** GET /api/api-keys/:id — one key with the permissions it holds. */
export interface ApiKeyDetail extends Omit<ApiKeyV2, "permissionCount"> {
  permissions: Array<{ id: string; description: string | null; category: string | null }>;
}

/** One key's detail, read when its sheet or permission editor opens. */
export function apiKeyDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["admin", "api-keys", "detail", id],
    queryFn: async (): Promise<ApiKeyDetail> => {
      const res = await fetch(`/api/api-keys/${id}`, { cache: "no-store" });

      if (!res.ok) throw new Error("Impossible de charger les détails de la clé.");

      // SAFETY: GET /api/api-keys/[id] returns the key detail with its permissions.
      return (await res.json()) as ApiKeyDetail;
    },
  });
}
